import React, { useState } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useReportData } from '../components/reports/useReportData';
import {
  DailyReport,
  MonthlyReport,
  SalesMarginReport,
  ShiftReport,
  LowMarginReport,
  RotationReport,
  BestsellersReport,
  SoldSearchReport,
  DailySalesReport,
  ForecastReport,
  WarehouseReport,
  DiscountsReport,
} from '../components/reports';

const ReportsPage = () => {
  const { locationId, selectedLocation } = useLocation();
  const { hasPermission } = usePermissions();

  // Uprawnienia raportów
  const canAccessReports = hasPermission('raporty', 'dostep', locationId);
  const canViewSalesReports = hasPermission('raporty', 'sprzedaz', locationId);
  const canViewWarehouseReports = hasPermission('raporty', 'magazyn', locationId);
  const canViewFinancialReports = hasPermission('raporty', 'finansowe', locationId);
  const canViewEmployeeReports = hasPermission('raporty', 'pracownicy', locationId);

  const [activeTab, setActiveTab] = useState('daily');

  // Parametry raportów produktowych
  const [reportParams, setReportParams] = useState({
    limit: 10,
    days: 30,
    metric: 'quantity',
    forecast_days: 30,
    analysis_days: 90
  });

  // Filtry dat (dla zakładek które korzystają ze wspólnych filtrów)
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Shared report data hook
  const reportData = useReportData(locationId);

  const {
    loading,
    error,
    setError,
    dailyStats,
    monthlyStats,
    topProducts,
    shiftHistory,
    lowMarginProducts,
    highestRotationProducts,
    lowestRotationProducts,
    bestsellingProducts,
    salesForecast,
    warehouseStock,
    marginReports,
    loadInitialData,
    loadProductReports,
    loadBestsellingProducts,
    loadSalesForecast,
    loadShiftHistory,
  } = reportData;

  if (loading) return <div className="container mt-4"><div className="text-center">Ładowanie...</div></div>;

  // Sprawdź czy użytkownik ma dostęp do modułu raportów
  if (!canAccessReports) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{
          backgroundColor: 'var(--warning-light)',
          color: 'var(--warning)',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid var(--warning)',
          maxWidth: '500px',
          margin: '2rem auto'
        }}>
          <h5 style={{ marginBottom: '0.5rem' }}>Brak dostępu</h5>
          <p style={{ margin: 0, fontSize: '13px' }}>
            Nie masz uprawnień do modułu Raporty. Skontaktuj się z administratorem.
          </p>
        </div>
      </div>
    );
  }

  // Tab definitions with permission filtering
  const tabs = [
    { id: 'daily', label: '📊 Statystyki', show: canViewSalesReports },
    { id: 'monthly', label: '📅 Miesięczne', show: canViewSalesReports },
    { id: 'sales_margin', label: '💰 Marża sprzedaży', show: canViewFinancialReports },
    { id: 'shifts', label: '📋 Zmiany', show: canViewEmployeeReports },
    { id: 'margin', label: '⚠️ Niska marża', show: canViewFinancialReports },
    { id: 'rotation', label: '🔄 Rotacja', show: canViewWarehouseReports },
    { id: 'bestsellers', label: '🏆 Bestsellery', show: canViewSalesReports },
    { id: 'sold_search', label: '🔍 Sprzedane produkty', show: canViewSalesReports },
    { id: 'daily_sales', label: '📋 Sprzedaż dzienna', show: canViewSalesReports },
    { id: 'forecast', label: '📈 Prognoza', show: canViewSalesReports },
    { id: 'warehouse', label: '📦 Magazyn', show: canViewWarehouseReports },
    { id: 'discounts', label: '🏷️ Rabaty', show: canViewSalesReports }
  ].filter(tab => tab.show);

  // Render the active report component
  const renderActiveReport = () => {
    switch (activeTab) {
      case 'daily':
        return <DailyReport dailyStats={dailyStats} topProducts={topProducts} />;
      case 'monthly':
        return <MonthlyReport monthlyStats={monthlyStats} />;
      case 'sales_margin':
        return <SalesMarginReport marginReports={marginReports} locationId={locationId} selectedLocation={selectedLocation} />;
      case 'shifts':
        return <ShiftReport shiftHistory={shiftHistory} onShiftClosed={loadShiftHistory} />;
      case 'margin':
        return <LowMarginReport lowMarginProducts={lowMarginProducts} />;
      case 'rotation':
        return <RotationReport highestRotationProducts={highestRotationProducts} lowestRotationProducts={lowestRotationProducts} reportParams={reportParams} />;
      case 'bestsellers':
        return <BestsellersReport bestsellingProducts={bestsellingProducts} reportParams={reportParams} setReportParams={setReportParams} loadBestsellingProducts={loadBestsellingProducts} />;
      case 'sold_search':
        return <SoldSearchReport locationId={locationId} />;
      case 'daily_sales':
        return <DailySalesReport locationId={locationId} selectedLocation={selectedLocation} />;
      case 'forecast':
        return <ForecastReport salesForecast={salesForecast} reportParams={reportParams} setReportParams={setReportParams} loadSalesForecast={loadSalesForecast} />;
      case 'warehouse':
        return <WarehouseReport warehouseStock={warehouseStock} />;
      case 'discounts':
        return <DiscountsReport selectedLocation={selectedLocation} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: '0.75rem', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Nagłówek */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Raporty i Statystyki</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {selectedLocation ? selectedLocation.nazwa : 'Wszystkie lokalizacje'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => {
                loadInitialData();
                if (['rotation', 'bestsellers', 'forecast', 'warehouse'].includes(activeTab)) {
                  loadProductReports(reportParams);
                }
              }}
            >
              Odśwież
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '0.5rem 0.75rem',
          marginBottom: '0.5rem',
          backgroundColor: 'var(--danger-light)',
          border: '1px solid var(--danger-light)',
          borderRadius: '4px',
          color: 'var(--danger)',
          fontSize: '0.75rem'
        }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ float: 'right', background: 'none',  cursor: 'pointer', color: 'var(--danger)' }}
          >×</button>
        </div>
      )}

      {/* Zakładki - styl POS */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.25rem',
        marginBottom: '0.5rem',
        backgroundColor: 'var(--card-bg)',
        padding: '0.35rem',
        borderRadius: '4px',
        border: '1px solid var(--border)'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: activeTab === tab.id ? 'var(--primary)' : 'var(--card-bg)',
              color: activeTab === tab.id ? '#fff' : 'var(--text-secondary)',
              border: activeTab === tab.id ? '1px solid var(--primary)' : '1px solid var(--border)',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? '600' : '500'
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtry - kompaktowy styl (ukryj dla zakładek z własnymi filtrami) */}
      {!['sold_search', 'discounts', 'warehouse', 'forecast', 'daily_sales'].includes(activeTab) && (
        <div style={{
          backgroundColor: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: '4px',
          marginBottom: '0.5rem',
          padding: '0.5rem 0.75rem'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem', alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '0.15rem', display: 'block' }}>Data od:</label>
              <input
                type="date"
                style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
                value={dateFilter.startDate}
                onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '0.15rem', display: 'block' }}>Data do:</label>
              <input
                type="date"
                style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
                value={dateFilter.endDate}
                onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
              />
            </div>

            {/* Dodatkowe filtry dla raportów produktowych */}
            {(['rotation', 'bestsellers', 'forecast'].includes(activeTab)) && (
              <>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '0.15rem', display: 'block' }}>Okres (dni):</label>
                  <input
                    type="number"
                    style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
                    value={reportParams.days}
                    onChange={(e) => setReportParams({...reportParams, days: parseInt(e.target.value) || 30})}
                    min="1"
                    max="365"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '0.15rem', display: 'block' }}>Limit:</label>
                  <select
                    style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
                    value={reportParams.limit}
                    onChange={(e) => setReportParams({...reportParams, limit: parseInt(e.target.value)})}
                  >
                    <option value={10}>Top 10</option>
                    <option value={20}>Top 20</option>
                    <option value={30}>Top 30</option>
                    <option value={50}>Top 50</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Zawartość zakładek */}
      <div>
        {renderActiveReport()}
      </div>
    </div>
  );
};

export default ReportsPage;
